import { useEffect, useRef, useState } from 'react';
import type { MailSort, MailView, ProcessedFilter } from '@jerome2/shared';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { tasksStore } from '../state/tasks.ts';
import { mailStore } from '../state/mail.ts';
import { Icon } from '../ui/Icon.tsx';
import { Chip, IconButton } from '../ui/primitives.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { ContextMenu, type MenuEntry } from './ContextMenu.tsx';

// Filterbalk boven de lijst: Gevlagd, Ongelezen, Verwerkt (met Klaar/Vraag/Bezig/Mislukt),
// gekozen map, sortering en zoeken in de hele mailbox. Op de telefoon: mappenknop, de drie
// weergaven als compacte chips en één zoekknop die een vel met zoekveld en sortering opent; het
// Verwerkt-filter, de mapchip en de lopende zoekopdracht staan dan op een tweede regel.
// Op de desktop staat rechts naast Sorteren het menu Weergave (ticket 28625) met de regels die het scherm meegeeft
// (`viewMenu`): nu de lijst verbergen of tonen; zo kunnen er regels bij zonder dat de balk verandert.

export interface Filters {
  view: MailView;
  folder: string;
  sort: MailSort;
  processed: ProcessedFilter;
  q: string;
}

export const SORT_LABELS: Record<MailSort, string> = { newest: 'Nieuwste eerst', oldest: 'Oudste eerst', urgent: 'Urgentste eerst' };

/** `viewMenu`: de regels van het menu Weergave (desktop); zonder regels geen menu. */
export function FilterBar(props: { filters: Filters; onChange: (patch: Partial<Filters>) => void; onToggleFolders?: (() => void) | undefined; foldersOpen: boolean; viewMenu?: MenuEntry[] | undefined }) {
  const narrow = useStore(app, (s) => s.narrow);
  const counts = useStore(tasksStore, (s) => s.counts);
  const folderName = useStore(mailStore, (s) => s.folders.find((f) => f.id === props.filters.folder)?.name ?? null);
  const [q, setQ] = useState(props.filters.q);
  const [searchSheet, setSearchSheet] = useState(false);
  // Het menu Weergave open: de plek van de knop, waaronder het hangt.
  const [viewAnchor, setViewAnchor] = useState<DOMRect | null>(null);
  useEffect(() => setQ(props.filters.q), [props.filters.q]);
  // De wachttijd hangt alleen aan de tekst: `props` wisselt elke render (tellers, lijst) en zou de
  // timer steeds opnieuw starten, zodat zoeken tijdens het laden nooit afging.
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });
  useEffect(() => {
    if (q === latest.current.filters.q) return;
    const t = setTimeout(() => {
      const p = latest.current;
      p.onChange(q.trim() ? { q, view: 'search' } : { q: '', view: p.filters.view === 'search' ? 'flagged' : p.filters.view });
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const f = props.filters;
  const processedTotal = counts.done + counts.question + counts.running + counts.failed;
  const setView = (view: MailView) => props.onChange({ view, q: '' });
  const searching = f.view === 'search' && f.q.trim() !== '';
  const submitSearch = () => {
    props.onChange(q.trim() ? { q: q.trim(), view: 'search' } : { q: '' });
    setSearchSheet(false);
  };

  const folderButton = props.onToggleFolders ? (
    <button type="button" aria-label="Mappen" aria-pressed={props.foldersOpen} className={`${narrow ? 'h-11 w-10' : 'h-9 w-9'} shrink-0 rounded-md flex items-center justify-center ${props.foldersOpen ? 'bg-tint text-ink' : 'text-ink-soft hover:bg-tint'}`} onClick={props.onToggleFolders}>
      <Icon name="folder" size={narrow ? 20 : 18} />
    </button>
  ) : null;
  const viewChips = (
    <div className={`flex items-center gap-0.5 rounded-full bg-paper p-0.5 ${narrow ? 'shrink-0' : ''}`} role="group" aria-label="Filter">
      <Chip compact={narrow} active={f.view === 'flagged'} onClick={() => setView('flagged')} count={counts.flagged}>
        Gevlagd
      </Chip>
      <Chip compact={narrow} active={f.view === 'unread'} onClick={() => setView('unread')} count={counts.unread}>
        Ongelezen
      </Chip>
      <Chip compact={narrow} active={f.view === 'processed'} onClick={() => setView('processed')} count={processedTotal}>
        Verwerkt
      </Chip>
    </div>
  );
  const processedChips =
    f.view === 'processed' ? (
      <div className="flex items-center gap-0.5 shrink-0" role="group" aria-label="Verwerkt-filter">
        <Chip compact={narrow} tone="blue" active={f.processed === 'done'} onClick={() => props.onChange({ processed: f.processed === 'done' ? 'all' : 'done' })} count={counts.done}>
          Klaar
        </Chip>
        <Chip compact={narrow} tone="amber" active={f.processed === 'question'} onClick={() => props.onChange({ processed: f.processed === 'question' ? 'all' : 'question' })} count={counts.question}>
          Vraag
        </Chip>
        <Chip compact={narrow} active={f.processed === 'running'} onClick={() => props.onChange({ processed: f.processed === 'running' ? 'all' : 'running' })} count={counts.running}>
          Bezig
        </Chip>
        <Chip compact={narrow} tone="red" active={f.processed === 'failed'} onClick={() => props.onChange({ processed: f.processed === 'failed' ? 'all' : 'failed' })} count={counts.failed}>
          Mislukt
        </Chip>
      </div>
    ) : null;
  const folderChip =
    f.folder !== 'all' && folderName ? (
      <span className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-tint text-[13px] text-ink shrink-0">
        <Icon name="folder" size={13} /> {folderName}
        <button type="button" aria-label="Mapfilter weg" className={`${narrow ? 'h-7 w-7' : 'h-6 w-6'} rounded-full flex items-center justify-center hover:bg-tint-2`} onClick={() => props.onChange({ folder: 'all', view: f.view === 'folder' ? 'flagged' : f.view })}>
          <Icon name="x" size={12} />
        </button>
      </span>
    ) : null;

  if (narrow) {
    return (
      <div className="shrink-0 border-b border-line bg-surface">
        <div className="h-14 flex items-center gap-1 px-2">
          {folderButton}
          <div className="flex-1 min-w-0 overflow-x-auto scroll-hidden flex items-center">{viewChips}</div>
          <button type="button" aria-label="Zoeken en sorteren" aria-pressed={searching} onClick={() => setSearchSheet(true)} className={`h-11 w-10 shrink-0 rounded-md flex items-center justify-center ${searching ? 'bg-tint text-accent' : 'text-ink-soft active:bg-tint'}`}>
            <Icon name="search" size={20} />
          </button>
        </div>
        {processedChips || folderChip || searching ? (
          <div className="flex items-center gap-2 px-3 pb-2 overflow-x-auto scroll-hidden">
            {processedChips}
            {folderChip}
            {searching ? (
              <span className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-tint text-[13px] text-ink shrink-0 max-w-full">
                <Icon name="search" size={13} /> <span className="truncate">{f.q}</span>
                <button type="button" aria-label="Zoekopdracht weg" className="h-7 w-7 rounded-full flex items-center justify-center" onClick={() => setQ('')}>
                  <Icon name="x" size={12} />
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
        {searchSheet ? (
          <Sheet title="Zoeken en sorteren" onClose={() => setSearchSheet(false)}>
            <div className="px-4 pt-3 pb-4 flex flex-col gap-4">
              <label className="relative block">
                <Icon name="search" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="search"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
                  placeholder="Zoek in je hele mailbox"
                  aria-label="Zoeken"
                  enterKeyHint="search"
                  className="h-12 w-full pl-11 pr-3 rounded-xl border border-line bg-paper placeholder:text-muted focus:border-accent focus:bg-surface focus:outline-none"
                />
              </label>
              <div>
                <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">Sorteren</div>
                <div role="radiogroup" aria-label="Sorteren" className="rounded-xl bg-paper overflow-hidden">
                  {(Object.keys(SORT_LABELS) as MailSort[]).map((value) => (
                    <button key={value} type="button" role="radio" aria-checked={f.sort === value} onClick={() => props.onChange({ sort: value })} className="w-full h-12 flex items-center gap-3 px-4 text-left text-[15px] border-b border-line last:border-b-0 active:bg-tint">
                      <span className="flex-1">{SORT_LABELS[value]}</span>
                      {f.sort === value ? <Icon name="check" size={18} className="text-accent" /> : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Sheet>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-14 shrink-0 flex items-center gap-2 px-3 border-b border-line bg-surface">
      {folderButton}
      {viewChips}
      {processedChips}
      {folderChip}
      <div className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-1 text-[13px] text-ink-soft">
          <Icon name="sort" size={14} />
          <select aria-label="Sorteren" value={f.sort} onChange={(e) => props.onChange({ sort: e.target.value as MailSort })} className="bg-transparent h-8 pr-1 rounded-md hover:bg-tint focus:outline-none">
            {(Object.keys(SORT_LABELS) as MailSort[]).map((value) => (
              <option key={value} value={value}>
                {SORT_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        {props.viewMenu?.length ? (
          <>
            {/* Menuknop: aria-expanded zegt of het menu open is, dus geen aria-pressed. */}
            <IconButton icon="layout" label="Weergave" aria-haspopup="menu" aria-expanded={viewAnchor !== null} active={viewAnchor !== null} aria-pressed={undefined} onClick={(e) => setViewAnchor(viewAnchor ? null : e.currentTarget.getBoundingClientRect())} />
            {viewAnchor ? <ContextMenu anchor={viewAnchor} items={props.viewMenu} label="Weergave" onClose={() => setViewAnchor(null)} /> : null}
          </>
        ) : null}
        <label className="relative">
          <Icon name="search" size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') props.onChange(q.trim() ? { q: q.trim(), view: 'search' } : { q: '' });
              if (e.key === 'Escape') setQ('');
            }}
            placeholder="Zoek in je hele mailbox"
            aria-label="Zoeken"
            className="h-9 w-[150px] md:w-[260px] pl-8 pr-3 rounded-full border border-line bg-paper text-[13px] placeholder:text-muted focus:border-accent focus:bg-surface focus:outline-none"
          />
        </label>
      </div>
    </div>
  );
}
